import { request as __request } from '../core/request.js';
export class OrdersService {
    /**
     * Create an order by mass
     * Create an order to purchase carbon offset by specifying a mass in tonnes or kilograms.
     * @param requestBody
     * @returns OrderByQuantity Order created successfully.
     * The response returns an Order object.
     *
     */
    createOrderByMass(requestBody) {
        return __request(this.client, this.config, {
            method: 'POST',
            url: '/orders/by-mass',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized. The API Key is invalid or disabled.`,
                409: `Conflict

                Examples:
                1. account is suspended
                2. order idempotency failure: an order with the same idempotency_key has already by created
                `,
                415: `The request is not an application/json encoded request`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Create an order by value
     * Create an order to purchase carbon offset by specifying a maximum purchase value.
     * @param requestBody
     * @returns OrderByValue Order created successfully.
     * The response returns an Order object.
     *
     */
    createOrderByValue(requestBody) {
        return __request(this.client, this.config, {
            method: 'POST',
            url: '/orders/by-value',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized. The API Key is invalid or disabled.`,
                409: `Conflict

                Examples:
                1. account is suspended
                2. order idempotency failure: an order with the same idempotency_key has already by created
                `,
                415: `The request is not an application/json encoded request`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Create an order by estimate id
     * Create an order to purchase carbon offset by specifying an estimate id
     * @param requestBody
     * @returns OrderByEstimate Order created successfully.
     * The response returns an Order object.
     *
     */
    createOrderByEstimate(requestBody) {
        return __request(this.client, this.config, {
            method: 'POST',
            url: '/orders/by-estimate',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized. The API Key is invalid or disabled.`,
                409: `Conflict

                Examples:
                1. account is suspended
                2. order idempotency failure: an order with the same idempotency_key has already by created
                3. order for this emission estimate has already been placed
                `,
                415: `The request is not an application/json encoded request`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * List orders
     * Returns paginated account orders ordered by creation date descending
     *
     * The API key used to access this method affects what orders are returned: test orders for the test
     * API key, live orders for the live one.
     *
     * @param limit Maximum number of resources to return, between 1 and 100.
     *
     * @param after A cursor for use in pagination.
     *
     * *after* is an object ID that defines your place in the list.
     *
     * For instance, if you make a list request and receive 100 objects, ending with *foo*, your subsequent call can include *after=foo* in order to fetch the next page of the list.
     *
     * @param offsetLinkId Filter orders by offset_link_id.
     *
     *
     * If offset_link_id is set, the response will only include orders belonging to the particular offset link.
     *
     * @returns PaginatedOrders The response returns paginated orders
     */
    listOrders(limit = '10', after, offsetLinkId) {
        return __request(this.client, this.config, {
            method: 'GET',
            url: '/orders',
            query: {
                limit: limit,
                after: after,
                offset_link_id: offsetLinkId,
            },
            errors: {
                401: `Unauthorized. The API Key is invalid or disabled.`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Get an order
     * Returns an order by id if it exists.
     *
     * @param id The order's unique identifier
     * @returns Order The response returns an order
     */
    getOrder(id) {
        return __request(this.client, this.config, {
            method: 'GET',
            url: '/orders/{id}',
            path: {
                id: id,
            },
            errors: {
                404: `The order does not exist`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Get a carbon offset certificate
     * Download a Carbon Offset Certificate for a completed order.
     *
     * @param id The order's unique identifier
     * @returns binary The response returns the Carbon Offset Certificate
     */
    getOrderCertificate(id) {
        return __request(this.client, this.config, {
            method: 'GET',
            url: '/orders/{id}/certificate',
            path: {
                id: id,
            },
            errors: {
                404: `The order or the certificate does not exist`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Get an order by idempotency key
     * Returns an order by idempotency key if it exists.
     *
     * @param idempotencyKey The order's idempotency key
     * @returns Order The response returns an order
     */
    getOrderByIdempotencyKey(idempotencyKey) {
        return __request(this.client, this.config, {
            method: 'GET',
            url: '/orders/by-idempotency-key/{idempotencyKey}',
            path: {
                idempotencyKey: idempotencyKey,
            },
            errors: {
                404: `The order does not exist`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Calculate an order quote by mass
     * This endpoint does not create an order.
     *
     * However, it processes the order as if it were placed returning estimated cost and bundles allocations.
     *
     * @param requestBody
     * @returns OrderQuoteByQuantity Order quote processed successfully.
     *
     */
    getOrderQuoteByMass(requestBody) {
        return __request(this.client, this.config, {
            method: 'POST',
            url: '/orders/by-mass/quote',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized. The API Key is invalid or disabled.`,
                409: `Conflict

                Examples:
                1. quantity not positive: the calculated quote results in non positive quantity
                `,
                415: `The request is not an application/json encoded request`,
                429: `Rate limit exceeded`,
            },
        });
    }
    /**
     * Calculate an order quote by value
     * This endpoint does not create an order.
     *
     * However, it processes the order as if it were placed returning estimated cost and bundles allocations.
     *
     * @param requestBody
     * @returns OrderQuoteByValue Order quote processed successfully.
     *
     */
    getOrderQuoteByValue(requestBody) {
        return __request(this.client, this.config, {
            method: 'POST',
            url: '/orders/by-value/quote',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Unauthorized. The API Key is invalid or disabled.`,
                409: `Conflict

                Examples:
                1. quantity not positive: the calculated quote results in non positive quantity
                `,
                415: `The request is not an application/json encoded request`,
                429: `Rate limit exceeded`,
            },
        });
    }
}
//# sourceMappingURL=OrdersService.js.map